import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useTranslationWithFallback from 'hooks/use-translation-with-fallback';

// Context
import { useToastContext } from 'context/toast-context';

// Store
import {
  selectCurrentRestaurant,
  selectIsSavingOrdersState,
  setRestaurantOrdersState
} from 'store/slices/restaurantsSlice';

// Utils
import bemify from 'utils/bemify';
import getTimezoneForCountry from 'utils/country-timezone';
import { getNextMidnightForTimezone, isOrderingPaused } from 'utils/orders-settings';

// Components
import Button from 'components/button';
import Switch from 'components/switch';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const SidebarOrdersControlCard = ({ className = null }) => {
  const { t } = useTranslationWithFallback('sidebar');

  const dispatch = useDispatch();
  const { showToast } = useToastContext();

  const currentRestaurant = useSelector(selectCurrentRestaurant);
  const isSaving = useSelector(selectIsSavingOrdersState);

  const ordersEnabled = currentRestaurant?.ordersEnabled !== false;
  const isPaused = useMemo(
    () => isOrderingPaused(currentRestaurant?.ordersPausedUntil),
    [currentRestaurant?.ordersPausedUntil]
  );

  const statusLabel = (() => {
    if (!ordersEnabled) return t('orders_control.status.off');
    if (isPaused) return t('orders_control.status.paused');

    return t('orders_control.status.accepting');
  })();

  const statusModifier = (() => {
    if (!ordersEnabled) return 'off';
    if (isPaused) return 'paused';

    return 'accepting';
  })();

  if (!currentRestaurant?.id) return null;

  const persistOrdersState = async (payload, errorMessage) => {
    const result = await dispatch(setRestaurantOrdersState({
      restaurantId: currentRestaurant.id,
      ...payload
    }));

    if (result?.error) {
      showToast(errorMessage, 'error');
    }
  };

  const handleToggleOrders = (checked) => {
    persistOrdersState({ ordersEnabled: checked }, t('orders_control.toasts.error'));
  };

  const handlePauseForToday = () => {
    const timeZone = getTimezoneForCountry(currentRestaurant.country);

    persistOrdersState(
      { pause: true, pauseUntil: getNextMidnightForTimezone(timeZone) },
      t('orders_control.toasts.error')
    );
  };

  const handleResume = () => {
    persistOrdersState({ pause: false }, t('orders_control.toasts.error'));
  };

  return (
    <div className={`${styles.sidebarOrdersControlCard}${className ? ` ${className}` : ''}`}>
      <div className={styles.sidebarOrdersControlCard__header}>
        <span className={styles.sidebarOrdersControlCard__title}>
          {t('orders_control.title')}
        </span>
        <span
          className={`${styles.sidebarOrdersControlCard__status} ${styles[`sidebarOrdersControlCard__status_${statusModifier}`]}`}
        >
          {statusLabel}
        </span>
      </div>
      <Switch
        checked={ordersEnabled}
        disabled={isSaving}
        id="sidebar-orders-enabled"
        label={t('orders_control.toggle_label')}
        onCheckedChange={handleToggleOrders}
      />
      {isPaused ?
        (
          <Button
            className={styles.sidebarOrdersControlCard__action}
            disabled={isSaving}
            size="s"
            text={t('orders_control.resume_button')}
            theme="grey"
            onClick={handleResume}
          />
        ) :
        (
          <Button
            className={styles.sidebarOrdersControlCard__action}
            disabled={isSaving || !ordersEnabled}
            size="s"
            text={t('orders_control.pause_button')}
            theme="grey"
            onClick={handlePauseForToday}
          />
        )}
    </div>
  );
};

SidebarOrdersControlCard.propTypes = {
  className: PropTypes.string
};

export default SidebarOrdersControlCard;
